/**
 * EditSession store Provider — page-scope.
 * 마운트할 때마다 새 store 인스턴스가 생기고, 언마운트 시 변경사항이 사라진다.
 *
 * 화면 이탈 시 변경사항을 유지할 필요가 없다는 정책의 구조적 표현.
 * 미저장 변경 보호는 useUnsavedChangesGuard(다른 PR에서 추가)에서 책임진다.
 */

import { createContext, useContext, useRef, type ReactNode } from "react";
import { useStore } from "zustand";
import {
  createEditSessionStore,
  type EditSessionState,
  type EditSessionStore,
} from "./edit-session-store";

const EditSessionStoreContext = createContext<EditSessionStore | null>(null);

interface EditSessionStoreProviderProps {
  children: ReactNode;
}

export const EditSessionStoreProvider = ({
  children,
}: EditSessionStoreProviderProps) => {
  const storeRef = useRef<EditSessionStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = createEditSessionStore();
  }
  return (
    <EditSessionStoreContext.Provider value={storeRef.current}>
      {children}
    </EditSessionStoreContext.Provider>
  );
};

export const useEditSessionStore = <T,>(
  selector: (state: EditSessionState) => T,
): T => {
  const store = useContext(EditSessionStoreContext);
  if (!store) {
    throw new Error(
      "useEditSessionStore must be used within EditSessionStoreProvider",
    );
  }
  return useStore(store, selector);
};
